const CURRENCY_CODE_PATTERN = /^[A-Z]{3}$/;
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export const WORKFLOW_ONLY_RESERVATION_CHARGE_CODES = new Set([
  "cancellation_penalty",
  "no_show_penalty",
  "early_departure_charge",
  "room_upgrade",
]);

export const isWorkflowOnlyReservationChargeType = (type) =>
  WORKFLOW_ONLY_RESERVATION_CHARGE_CODES.has(
    String(type?.code || "").trim().toLowerCase()
  );

export const reservationChargeCreatePath = (reservationId, workflowKind) => {
  const id = Number(reservationId);
  if (!Number.isInteger(id) || id <= 0) return null;
  const normalizedWorkflowKind = String(workflowKind || "").trim();
  if (!normalizedWorkflowKind) {
    return `/api/reservation-charges/reservations/${id}`;
  }
  const workflowPaths = {
    cancel: "cancellation",
    no_show: "no_show",
  };
  const workflow = workflowPaths[normalizedWorkflowKind];
  return workflow
    ? `/api/reservations/${id}/terminal-workflows/${workflow}`
    : null;
};

export const createReservationChargeOperationId = (cryptoSource) => {
  const resolvedCrypto =
    cryptoSource || (typeof window !== "undefined" ? window.crypto : null);
  if (typeof resolvedCrypto?.randomUUID === "function") {
    return resolvedCrypto.randomUUID().toLowerCase();
  }

  const bytes = new Uint8Array(16);
  if (typeof resolvedCrypto?.getRandomValues === "function") {
    resolvedCrypto.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (value) =>
    value.toString(16).padStart(2, "0")
  ).join("");
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20),
  ].join("-");
};

export const reservationChargeIdempotencyConfig = (operationId) => {
  const normalizedOperationId = String(operationId || "")
    .trim()
    .toLowerCase();
  if (!UUID_PATTERN.test(normalizedOperationId)) {
    throw new Error("A valid reservation charge operation ID is required.");
  }
  return {
    headers: {
      "Idempotency-Key": normalizedOperationId,
    },
  };
};

const normalizeCurrencyCode = (value) => {
  const currencyCode = String(value || "").trim().toUpperCase();
  return CURRENCY_CODE_PATTERN.test(currencyCode) ? currencyCode : null;
};

export const resolveCategoryMoveBaseCurrency = (...values) => {
  for (const value of values) {
    const currencyCode = normalizeCurrencyCode(value);
    if (currencyCode) return currencyCode;
  }
  return null;
};

export const buildCategoryMoveOverrideCurrencyFields = ({
  overrides,
  baseCurrencyCode,
} = {}) => {
  const usesAbsoluteOverride = (Array.isArray(overrides) ? overrides : []).some(
    (override) =>
      String(override?.override_type || "").trim().toLowerCase() ===
      "absolute"
  );
  if (!usesAbsoluteOverride) return {};

  const normalizedBaseCurrency = normalizeCurrencyCode(baseCurrencyCode);
  if (!normalizedBaseCurrency) {
    throw new Error(
      "The reservation base currency could not be determined. Close this window and try again."
    );
  }

  return { override_currency_code: normalizedBaseCurrency };
};

export const readRatesPricesDeepLink = (searchParams) => {
  const params =
    searchParams instanceof URLSearchParams
      ? searchParams
      : new URLSearchParams(searchParams || "");
  const action = String(params.get("open_action") || "")
    .trim()
    .toLowerCase();
  if (action !== "rates_prices") return null;

  const reservationId = Number(params.get("reservation_id"));
  return {
    action,
    reservationId:
      Number.isInteger(reservationId) && reservationId > 0
        ? reservationId
        : null,
  };
};

export const getActiveBusinessUnitReservationError = ({
  activeBusinessUnitId,
  reservationBusinessUnitId,
} = {}) => {
  const activeId = Number(activeBusinessUnitId);
  if (!Number.isInteger(activeId) || activeId <= 0) {
    return "Select an active business unit before changing this reservation.";
  }

  const reservationId = Number(reservationBusinessUnitId);
  if (
    Number.isInteger(reservationId) &&
    reservationId > 0 &&
    reservationId !== activeId
  ) {
    return "This reservation belongs to another business unit. Switch to that business unit before changing it.";
  }

  return "";
};

export const getReservationModificationErrorMessage = (
  err,
  fallback = "Modify Stay could not be completed."
) => {
  const payload = err?.response?.data || {};
  const code = String(payload?.code || "").trim().toUpperCase();
  const backendMessage = payload?.message || payload?.error || "";

  if (code === "ACTIVE_BUSINESS_UNIT_REQUIRED") {
    return "Select an active business unit and try again.";
  }
  if (code === "BUSINESS_UNIT_SCOPE_MISMATCH") {
    return "This reservation belongs to another business unit. Switch to that business unit and try again.";
  }
  if (
    code === "CATEGORY_MOVE_STAY_NIGHTS_INCOMPLETE" ||
    /nightly stay state is incomplete/i.test(String(backendMessage))
  ) {
    return "This older reservation is missing part of its nightly stay data. Reload the reservation and try again; if it still fails, ask an administrator to rebuild its nightly stay records.";
  }
  if (
    code === "CATEGORY_MOVE_OVERRIDE_CURRENCY_REQUIRED" ||
    code === "CATEGORY_MOVE_OVERRIDE_CURRENCY_MISMATCH"
  ) {
    return "The reservation currency changed while the price was being reviewed. Close this window, reopen it and apply the override again.";
  }

  return backendMessage || err?.message || fallback;
};

const GROUP_CANCELLATION_MUTABLE_STATUSES = new Set([
  "draft",
  "requested",
  "confirmed",
]);

export const getGroupCancellationGate = ({
  reservations,
  canUpdateReservations,
} = {}) => {
  if (!canUpdateReservations) {
    return {
      allowed: false,
      reason: "You do not have permission to cancel reservations.",
    };
  }

  const rows = Array.isArray(reservations) ? reservations : [];
  if (!rows.length) {
    return {
      allowed: false,
      reason: "This reservation group has no rooms to cancel.",
    };
  }

  const statuses = rows.map((reservation) =>
    String(reservation?.status || "").trim().toLowerCase()
  );
  const blockedStatuses = [
    ...new Set(
      statuses.filter(
        (status) =>
          !GROUP_CANCELLATION_MUTABLE_STATUSES.has(status) &&
          status !== "cancelled"
      )
    ),
  ];
  if (blockedStatuses.length) {
    return {
      allowed: false,
      reason:
        "The group can only be cancelled when every room is Draft, Requested, Confirmed, or already Cancelled.",
      blockedStatuses,
    };
  }

  if (!statuses.some((status) => GROUP_CANCELLATION_MUTABLE_STATUSES.has(status))) {
    return {
      allowed: false,
      reason: "This reservation group is already fully cancelled.",
    };
  }

  return { allowed: true, reason: "" };
};
